import type { Me } from "@divespots/contracts";
import { isStaff } from "@divespots/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { getApiMode } from "../api/config.ts";
import { ApiRequestError, isApiRequestError } from "../api/errors.ts";
import { clearPrivateCaches } from "../pwa/update-store.ts";

export type SessionUser = Me["user"];

export interface Session {
  /** `loading` until /api/me answered; `anonymous` also covers non-staff sessions (403). */
  status: "loading" | "anonymous" | "user";
  /** Any signed-in account: a diver as well as staff. */
  user: SessionUser | null;
  /** Moderator or admin: the only ones who may see the admin UI or edit directly. */
  staff: boolean;
}

export const ME_KEY = ["me"] as const;

/** `GET /api/me`: the signed-in account (any role), or null. 401 (anonymous) and 403 (banned) both mean "no session here". */
export async function fetchMe(signal?: AbortSignal): Promise<SessionUser | null> {
  try {
    return (await api.me(signal)).user;
  } catch (e) {
    if (isApiRequestError(e) && (e.code === "unauthorized" || e.code === "forbidden")) return null;
    throw e;
  }
}

export function useSession(): Session {
  const q = useQuery({
    queryKey: ME_KEY,
    queryFn: ({ signal }) => fetchMe(signal),
    retry: false,
    staleTime: 60_000,
  });
  if (q.isPending) return { status: "loading", user: null, staff: false };
  const user = q.data ?? null;
  return { status: user ? "user" : "anonymous", user, staff: user ? isStaff(user.role) : false };
}

async function authPost(path: string, body: unknown): Promise<Response> {
  try {
    return await fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new ApiRequestError("network", e instanceof Error ? e.message : "network error");
  }
}

/** `strict`: a non-2xx answer is an error too (the session may still be alive). */
export async function signOut(opts?: { strict?: boolean }): Promise<void> {
  if (import.meta.env.VITE_API_MODE !== "live") {
    return (await import("../auth/fixture-auth.ts")).signOut();
  }
  const res = await authPost("/api/auth/sign-out", {});
  if (opts?.strict && !res.ok) {
    throw new ApiRequestError("internal", `sign-out failed: HTTP ${res.status}`, res.status);
  }
}

/** `divespots.passkeyOffer.<userId>`: the diver's "no thanks" must not outlive the session. */
export function forgetPasskeyOffers(): void {
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith("divespots.passkeyOffer")) localStorage.removeItem(k);
    }
  } catch {
    /* no storage */
  }
}

/** Everything that depends on who is looking must be refetched after login/logout. */
export function useSessionActions() {
  const qc = useQueryClient();
  return {
    /** Refetches who is signed in and returns them. */
    async afterSignIn(): Promise<SessionUser | null> {
      clearPrivateCaches();
      qc.clear(); // drafts or moderator-only data must never outlive a session change
      return qc.fetchQuery({ queryKey: ME_KEY, queryFn: ({ signal }) => fetchMe(signal) });
    },
    /**
     * Signs out and forgets everything that depended on the session. `strict`: when the request
     * itself fails (offline, or a non-2xx answer), nothing is cleared and the error is thrown, so the UI does not claim
     * a sign-out that did not happen.
     */
    async signOutAndClear(opts?: { strict?: boolean }) {
      const clear = () => {
        clearPrivateCaches();
        forgetPasskeyOffers();
        qc.clear();
        qc.setQueryData(ME_KEY, null);
      };
      if (opts?.strict) {
        await signOut({ strict: true });
        return clear();
      }
      try {
        await signOut();
      } finally {
        clear();
      }
    },
  };
}
